import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

/**
 * AdminModal — single source of truth for every admin modal.
 *
 * Behavior:
 *   • Vertically + horizontally centered on every viewport (uses flex
 *     centering + fixed inset-0, NOT translate-based positioning, so
 *     long-content modals never overflow off-screen).
 *   • Locks <body> scroll while open, restores on close.
 *   • Dark overlay (`bg-black/55 backdrop-blur-sm`).
 *   • Click outside → close (configurable via `closeOnBackdrop`).
 *   • Esc key → close.
 *   • Modal panel max-h: 90vh, internal content scrolls (NOT the page or
 *     overlay).
 *   • Mobile (<sm): full-width bottom sheet with rounded top corners.
 *   • Desktop (≥sm): centered card with rounded corners and 16/24/32
 *     px gutter.
 *   • Z-index hierarchy: overlay 80, modal 90 — well above any sticky
 *     header or sidebar.
 *   • Animated entry/exit (framer-motion).
 *
 * Layout slots:
 *   • `title` (required)        — sticky top header with close button.
 *   • `subtitle` (optional)     — small text under the title.
 *   • `headerExtra` (optional)  — extra content in the header (e.g. dirty badge).
 *   • `children`                — body content; auto-scrolls when long.
 *   • `footer` (optional)       — sticky bottom action bar.
 *
 * Sizing presets via `size`:
 *   sm   → max-w-md   (~28rem)
 *   md   → max-w-lg   (~32rem)  default
 *   lg   → max-w-2xl  (~42rem)
 *   xl   → max-w-3xl  (~48rem)
 *   2xl  → max-w-4xl  (~56rem)
 */

const SIZE_MAP = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-3xl',
  '2xl': 'sm:max-w-4xl',
  '3xl': 'sm:max-w-5xl',
};

export const AdminModal = ({
  open,
  onClose,
  title,
  subtitle,
  headerExtra,
  children,
  footer,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEsc = true,
  hideCloseButton = false,
  testId = 'admin-modal',
  panelClassName = '',
}) => {
  useBodyScrollLock(open);

  const handleEsc = useCallback(
    (e) => {
      if (!closeOnEsc) return;
      if (e.key === 'Escape' && open) onClose?.();
    },
    [closeOnEsc, open, onClose]
  );

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [open, handleEsc]);

  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;

  // Portal mount target — document.body. Escapes all ancestor stacking
  // contexts and `will-change`/`transform` containing blocks (a common
  // cause of "modal not centered, scrolls with page, opens off-screen"
  // bugs in app shells that use page-transition wrappers).
  if (typeof document === 'undefined') return null;

  return createPortal((
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={closeOnBackdrop ? onClose : undefined}
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${testId}-title`}
          data-testid={testId}
        >
          <motion.div
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className={[
              'relative z-[90] bg-white shadow-2xl flex flex-col',
              'w-full sm:w-auto',
              sizeClass,
              'sm:mx-auto',
              'rounded-t-2xl sm:rounded-2xl',
              'max-h-[92vh] sm:max-h-[90vh]',
              panelClassName,
            ].join(' ')}
            onClick={(e) => e.stopPropagation()}
            data-testid={`${testId}-panel`}
          >
            {/* Header */}
            {(title || !hideCloseButton) && (
              <div className="flex items-start gap-3 px-5 sm:px-6 py-4 border-b border-slate-100">
                <div className="flex-1 min-w-0">
                  {title && (
                    <h2
                      id={`${testId}-title`}
                      className="text-[17px] sm:text-lg font-bold text-[#002249] leading-tight tracking-tight"
                    >
                      {title}
                    </h2>
                  )}
                  {subtitle && (
                    <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">{subtitle}</p>
                  )}
                </div>
                {headerExtra && <div className="flex items-center gap-2">{headerExtra}</div>}
                {!hideCloseButton && (
                  <button
                    onClick={onClose}
                    className="text-slate-400 hover:text-slate-700 transition-colors p-1 -m-1 rounded-md hover:bg-slate-100 flex-shrink-0"
                    aria-label="Close"
                    data-testid={`${testId}-close`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {/* Body — scrolls internally when long */}
            <div className="flex-1 overflow-y-auto px-5 sm:px-6 py-5 overscroll-contain" data-testid={`${testId}-body`}>
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div
                className="flex items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t border-slate-100 bg-slate-50/60 rounded-b-2xl"
                data-testid={`${testId}-footer`}
              >
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  ), document.body);
};

export default AdminModal;
